// src/components/sections/BlogSection.js
import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { fadeIn, scaleIn } from '../../styles/animations';
import SectionTitle from '../ui/SectionTitle';

const BlogWrapper = styled.section`
  background-color: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing.xl} 0;
`;

const BlogContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.md};
`;

const BlogIntro = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  animation: ${fadeIn} 0.8s ease-out forwards;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.xl};
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled.div`
  background-color: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${props => props.theme.shadows.small};
  border: 1px solid #f0f0f0;
  transition: all ${props => props.theme.transitions.medium};
  animation: ${scaleIn} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay || '0s'};
  opacity: 0;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: ${props => props.theme.shadows.medium};
    
    .blog-image img {
      transform: scale(1.05);
    }
  }
`;

const BlogImage = styled.div`
  height: 200px;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform ${props => props.theme.transitions.medium};
  }
`;

const BlogContent = styled.div`
  padding: ${props => props.theme.spacing.md};
`;

const BlogDate = styled.p`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
  font-weight: 600;
`;

const BlogTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.large};
  margin-bottom: ${props => props.theme.spacing.sm};
  
  a {
    color: ${props => props.theme.colors.textMain};
    transition: color ${props => props.theme.transitions.fast};
    
    &:hover {
      color: ${props => props.theme.colors.primary};
    }
  }
`;

const BlogExcerpt = styled.p`
  color: ${props => props.theme.colors.textLight};
  margin-bottom: ${props => props.theme.spacing.md};
  
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BlogLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  transition: all ${props => props.theme.transitions.fast};
  
  svg {
    margin-left: 0.5rem;
    transition: transform ${props => props.theme.transitions.fast};
  }
  
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    
    svg {
      transform: translateX(5px);
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${props => props.theme.spacing.xl};
  animation: ${fadeIn} 0.8s ease-out forwards;
  animation-delay: 0.6s;
  opacity: 0;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: transparent;
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: ${props => props.theme.borderRadius.medium};
  border: 2px solid ${props => props.theme.colors.primary};
  transition: all ${props => props.theme.transitions.fast};
  text-decoration: none;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.light};
  }
`;

const blogPosts = [
    {
        id: 1,
        image: "https://images.unsplash.com/photo-1595828071663-5e84e3025dcf?q=80&w=500",
        date: "15 Mars 2025",
        title: "Comment entretenir votre terrasse en bois ?",
        excerpt: "Découvrez nos conseils pour prolonger la durée de vie de votre terrasse et préserver sa beauté année après année. Un bon entretien est essentiel pour maintenir l'aspect et la qualité de votre installation.",
        slug: "comment-entretenir-terrasse-bois"
    },
    {
        id: 2,
        image: "https://images.unsplash.com/photo-1578715234781-a3cc79e134f6?q=80&w=500",
        date: "2 Mars 2025",
        title: "Nouvelle réalisation à La Baule",
        excerpt: "Découvrez notre dernière installation qui allie élégance et vue exceptionnelle sur l'océan. Un projet ambitieux qui a nécessité une expertise particulière dans le travail du bois exotique.",
        slug: "realisation-la-baule"
    },
    {
        id: 3,
        image: "https://images.unsplash.com/photo-1595423573597-527ed96d9c49?q=80&w=500",
        date: "20 Février 2025",
        title: "Les tendances terrasses pour 2025",
        excerpt: "Quelles sont les nouveautés et tendances en matière d'aménagement extérieur cette année ? Matériaux, formes, techniques, découvrez ce qui fera la différence pour votre espace extérieur.",
        slug: "tendances-terrasses-2025"
    }
];

const BlogSection = () => {
    return (
        <BlogWrapper id="blog">
            <BlogContainer>
                <BlogIntro>
                    <SectionTitle
                        text="Actualités & Blog"
                        subtitle="Découvrez nos derniers articles, conseils et réalisations."
                        centered
                    />
                </BlogIntro>

                <BlogGrid>
                    {blogPosts.map((post, index) => (
                        <BlogCard key={post.id} delay={`${0.2 * index}s`}>
                            <BlogImage className="blog-image">
                                <img src={post.image} alt={post.title} />
                            </BlogImage>
                            <BlogContent>
                                <BlogDate>{post.date}</BlogDate>
                                <BlogTitle>
                                    <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                                </BlogTitle>
                                <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                                <BlogLink to={`/blog/${post.slug}`}>
                                    Lire l'article
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                        <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z" />
                                    </svg>
                                </BlogLink>
                            </BlogContent>
                        </BlogCard>
                    ))}
                </BlogGrid>

                <ButtonContainer>
                    <CTAButton to="/blog">
                        Voir tous les articles
                    </CTAButton>
                </ButtonContainer>
            </BlogContainer>
        </BlogWrapper>
    );
};

export default BlogSection;